import { useMutation, useQuery } from '@tanstack/react-query'
import type { OfferRecipientCreateBody, OfferRecipientDTO } from '@today/models'
import type { ProspectFormValues, RecipientCandidateView } from '@today/ui-domain'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorCodeOf, formatDate } from '@/hooks/customers/customer-ui'
import { OFFERS_NAMESPACE } from '@/i18n'
import { offerRecipientService } from '@/services/offer-recipient.service'
import type { DraftRecipient } from './useOfferWizardDraft'

/** No search below this many characters (BR-3). */
export const RECIPIENT_TERM_MIN = 2
/** Keystrokes are debounced by this many milliseconds before a search is sent. */
export const RECIPIENT_SEARCH_DEBOUNCE_MS = 300

const EMPTY_PROSPECT: ProspectFormValues = {
	firstName: '',
	lastName: '',
	email: '',
	phone: '',
	marketingConsent: false,
}

/** `value`, once it has stopped changing for `delay` ms. */
function useDebounced<T>(value: T, delay: number): T {
	const [debounced, setDebounced] = useState(value)
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay)
		return () => clearTimeout(timer)
	}, [value, delay])
	return debounced
}

/** The prospect form → the API body: values are trimmed, blanks omitted, the consent choice always sent. */
export const toProspectBody = (values: ProspectFormValues): OfferRecipientCreateBody => ({
	firstName: values.firstName.trim(),
	...(values.lastName.trim() ? { lastName: values.lastName.trim() } : {}),
	...(values.email.trim() ? { email: values.email.trim() } : {}),
	...(values.phone.trim() ? { phone: values.phone.trim() } : {}),
	marketingConsent: values.marketingConsent,
})

const toCandidate = (dto: OfferRecipientDTO, locale: string): RecipientCandidateView => ({
	id: dto.id,
	name: dto.name,
	type: dto.type === 'lead' ? 'lead' : 'customer',
	lastOfferDate: dto.lastOfferDate ? formatDate(dto.lastOfferDate, locale) : null,
})

/**
 * Offer wizard Step 1 (TO-122 offer-recipient-selection REQ-010): a debounced,
 * role-scoped typeahead (no request under 2 characters) and an inline prospect
 * form. Selecting a candidate or creating a prospect sets the draft's recipient;
 * a refused prospect keeps the form open with the user's values.
 */
export function useRecipientStep(recipient: DraftRecipient | null, setRecipient: (r: DraftRecipient | null) => void) {
	const { t, i18n } = useTranslation(OFFERS_NAMESPACE)
	const [query, setQuery] = useState('')
	const [prospectOpen, setProspectOpen] = useState(false)
	const [prospect, setProspect] = useState<ProspectFormValues>(EMPTY_PROSPECT)
	const term = useDebounced(query.trim(), RECIPIENT_SEARCH_DEBOUNCE_MS)
	const canSearch = term.length >= RECIPIENT_TERM_MIN

	const search = useQuery({
		queryKey: ['offer-recipients', term],
		queryFn: () => offerRecipientService.search(term),
		enabled: canSearch,
	})
	const create = useMutation({
		mutationFn: (values: ProspectFormValues) => offerRecipientService.createProspect(toProspectBody(values)),
		onSuccess: (created) => {
			setRecipient({ id: created.id, name: created.name })
			setProspectOpen(false)
			setProspect(EMPTY_PROSPECT)
		},
	})

	const code = create.isError ? errorCodeOf(create.error) : null
	const prospectError = create.isError
		? t(code && i18n.exists(`errors.${code}`, { ns: OFFERS_NAMESPACE }) ? `errors.${code}` : 'errors.generic')
		: null

	return {
		t,
		query,
		searchHint: query.trim().length > 0 && query.trim().length < RECIPIENT_TERM_MIN,
		candidates: canSearch ? (search.data ?? []).map((dto) => toCandidate(dto, i18n.language)) : [],
		isSearching: canSearch && search.isFetching,
		selected: recipient,
		prospectOpen,
		prospect,
		prospectError,
		isCreating: create.isPending,
		onQueryChange: setQuery,
		onSelect: (candidate: RecipientCandidateView) => setRecipient({ id: candidate.id, name: candidate.name }),
		onClearSelection: () => setRecipient(null),
		onOpenProspect: () => setProspectOpen(true),
		onCancelProspect: () => {
			setProspectOpen(false)
			create.reset()
		},
		onProspectChange: (field: keyof ProspectFormValues, value: string | boolean) =>
			setProspect((current) => ({ ...current, [field]: value })),
		onSubmitProspect: () => create.mutate(prospect),
	}
}
